import React, { useState, useRef, useCallback, useEffect } from 'react'
import { X, GripVertical } from 'lucide-react'
import Button from '@/components/ui/Button'
import { designTokens } from '@/utils/design-tokens'
import { useAppSelector } from '@/store/hooks'
import { OrgMemberService } from '@/api/generated/services/OrgMemberService'
import { useAlert } from '@/components/ui/Alert/exports'
import type { WorkspaceResponse } from '@/api/generated/models/WorkspaceResponse'

interface WorkspaceManagementModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

interface WorkspaceItem extends WorkspaceResponse {
  isCurrentlySelected: boolean
}

interface DragState {
  isDragging: boolean
  draggedItemId: string | null
  dropTargetId: string | null
  dropPosition: 'before' | 'after' | null
}

const WorkspaceManagementModal: React.FC<WorkspaceManagementModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const { showSuccess, showError } = useAlert()
  const { workspaces, selectedWorkspace } = useAppSelector(state => state.workspace)
  const { orgMember } = useAppSelector(state => state.auth)

  // 로컬 상태
  const [localWorkspaces, setLocalWorkspaces] = useState<WorkspaceItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  // 드래그 상태
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    draggedItemId: null,
    dropTargetId: null,
    dropPosition: null
  })

  // Refs
  const dragDataRef = useRef<WorkspaceItem | null>(null)

  // 삭제 확인 상태
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean
    workspace: WorkspaceItem | null
  }>({ isOpen: false, workspace: null })

  // 모달 열릴 때 워크스페이스 목록 초기화
  useEffect(() => {
    if (isOpen) {
      const items: WorkspaceItem[] = workspaces
        .map(ws => ({
          ...ws,
          isCurrentlySelected: ws.id === selectedWorkspace?.id
        }))
        .sort((a, b) => a.sort_key - b.sort_key)
      setLocalWorkspaces(items)
      setHasChanges(false)
    }
  }, [isOpen, workspaces, selectedWorkspace?.id])

  // 드래그 시작
  const handleDragStart = useCallback((workspace: WorkspaceItem) => {
    dragDataRef.current = workspace
    setDragState({
      isDragging: true,
      draggedItemId: workspace.id,
      dropTargetId: null,
      dropPosition: null
    })
  }, [])

  // 드래그 종료
  const handleDragEnd = useCallback(() => {
    dragDataRef.current = null
    setDragState({
      isDragging: false,
      draggedItemId: null,
      dropTargetId: null,
      dropPosition: null
    })
  }, [])

  // 드래그 오버
  const handleDragOver = useCallback((e: React.DragEvent, targetWorkspace: WorkspaceItem) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'

    if (!dragDataRef.current || dragDataRef.current.id === targetWorkspace.id) {
      return
    }

    const rect = e.currentTarget.getBoundingClientRect()
    const y = e.clientY - rect.top
    const position = y < rect.height / 2 ? 'before' : 'after'

    setDragState(prev => ({
      ...prev,
      dropTargetId: targetWorkspace.id,
      dropPosition: position
    }))
  }, [])

  // 드래그 리브
  const handleDragLeave = useCallback(() => {
    setDragState(prev => ({
      ...prev,
      dropTargetId: null,
      dropPosition: null
    }))
  }, [])

  // 드롭 처리
  const handleDrop = useCallback(async (e: React.DragEvent, targetWorkspace: WorkspaceItem) => {
    e.preventDefault()

    const draggedWorkspace = dragDataRef.current
    if (!draggedWorkspace || draggedWorkspace.id === targetWorkspace.id) {
      handleDragEnd()
      return
    }

    const { dropPosition } = dragState
    if (!dropPosition) {
      handleDragEnd()
      return
    }

    // 새로운 sort_key 계산
    const sortedWorkspaces = [...localWorkspaces].sort((a, b) => a.sort_key - b.sort_key)
    const targetIndex = sortedWorkspaces.findIndex(ws => ws.id === targetWorkspace.id)

    let newSortKey: number
    if (dropPosition === 'before') {
      const prevItem = targetIndex > 0 ? sortedWorkspaces[targetIndex - 1] : null
      newSortKey = prevItem
        ? Math.floor((prevItem.sort_key + targetWorkspace.sort_key) / 2)
        : targetWorkspace.sort_key - 100000
    } else {
      const nextItem = targetIndex < sortedWorkspaces.length - 1 ? sortedWorkspaces[targetIndex + 1] : null
      newSortKey = nextItem
        ? Math.floor((targetWorkspace.sort_key + nextItem.sort_key) / 2)
        : targetWorkspace.sort_key + 100000
    }

    // 낙관적 업데이트
    const previousWorkspaces = [...localWorkspaces]
    const updatedWorkspaces = localWorkspaces.map(ws =>
      ws.id === draggedWorkspace.id ? { ...ws, sort_key: newSortKey } : ws
    ).sort((a, b) => a.sort_key - b.sort_key)

    setLocalWorkspaces(updatedWorkspaces)
    setHasChanges(true)
    handleDragEnd()

    // API 호출
    try {
      await OrgMemberService.appsOrganizationViewsUpdateOrgMemberWorkspaceSort({
        workspaceId: draggedWorkspace.id,
        requestBody: { sort_key: newSortKey }
      })
      showSuccess('워크스페이스 순서가 변경되었습니다.')
    } catch (err) {
      console.error('Failed to update workspace sort:', err)
      // 롤백
      setLocalWorkspaces(previousWorkspaces)
      showError('순서 변경에 실패했습니다.')
    }
  }, [dragState, localWorkspaces, handleDragEnd, showSuccess, showError])

  // 숨기기 클릭
  const handleHideClick = useCallback((workspace: WorkspaceItem) => {
    if (workspace.isCurrentlySelected) {
      showError('현재 선택된 워크스페이스는 숨길 수 없습니다.')
      return
    }
    setDeleteConfirm({ isOpen: true, workspace })
  }, [showError])

  // 숨기기 확인
  const handleConfirmHide = useCallback(async () => {
    const workspace = deleteConfirm.workspace
    if (!workspace || !orgMember?.id) {
      setDeleteConfirm({ isOpen: false, workspace: null })
      return
    }

    setIsLoading(true)
    try {
      await OrgMemberService.appsOrganizationViewsOrgMemberRemoveWorkspace({
        memberId: orgMember.id,
        workspaceId: workspace.id
      })

      // 목록에서 제거
      setLocalWorkspaces(prev => prev.filter(ws => ws.id !== workspace.id))
      setHasChanges(true)
      showSuccess('워크스페이스가 숨김 처리되었습니다.')
    } catch (err) {
      console.error('Failed to hide workspace:', err)
      showError('워크스페이스 숨김에 실패했습니다.')
    } finally {
      setIsLoading(false)
      setDeleteConfirm({ isOpen: false, workspace: null })
    }
  }, [deleteConfirm.workspace, orgMember?.id, showSuccess, showError])

  // 모달 닫기
  const handleClose = useCallback(() => {
    if (hasChanges) {
      onSuccess?.()
    }
    onClose()
  }, [hasChanges, onSuccess, onClose])

  // 배경 클릭
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose()
    }
  }

  if (!isOpen) return null

  return (
    <>
      {/* 메인 모달 */}
      <div
        className="fixed inset-0 z-[60] flex items-center justify-center"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        data-modal-overlay
        onMouseDown={(e) => e.stopPropagation()}
        onClick={handleBackdropClick}
      >
        <div
          className="bg-white rounded"
          style={{
            width: '500px',
            maxHeight: '80vh',
            padding: '20px',
            border: `1px solid ${designTokens.colors.gray[1]}`,
            borderRadius: '4px',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Header */}
          <div
            className="flex justify-between items-center flex-shrink-0"
            style={{
              padding: '7px 0',
              borderBottom: `1px solid ${designTokens.colors.gray[1]}`,
              marginBottom: '16px'
            }}
          >
            <h2
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '24px',
                fontWeight: 700,
                lineHeight: '1.193359375em',
                color: designTokens.colors.gray[6]
              }}
            >
              워크스페이스 관리
            </h2>
            <button
              onClick={handleClose}
              className="p-0 border-0 bg-transparent cursor-pointer"
              style={{
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <X
                size={12}
                style={{
                  color: designTokens.colors.gray[5],
                  strokeWidth: 2
                }}
              />
            </button>
          </div>

          {/* Content */}
          <div
            className="flex-1 overflow-y-auto"
            style={{ marginBottom: '16px' }}
          >
            {localWorkspaces.length === 0 ? (
              <div
                className="text-center py-8"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '14px',
                  color: designTokens.colors.gray[4]
                }}
              >
                소속된 워크스페이스가 없습니다.
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {localWorkspaces.map(workspace => (
                  <div
                    key={workspace.id}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'move'
                      e.dataTransfer.setData('text/plain', workspace.id)
                      handleDragStart(workspace)
                    }}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleDragOver(e, workspace)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, workspace)}
                    className="flex items-center gap-3 px-3 py-2 rounded transition-colors"
                    style={{
                      border: `1px solid ${designTokens.colors.gray[1]}`,
                      backgroundColor: dragState.draggedItemId === workspace.id
                        ? designTokens.colors.gray[0]
                        : workspace.isCurrentlySelected
                          ? designTokens.colors.primary.light + '20'
                          : designTokens.colors.white,
                      opacity: dragState.draggedItemId === workspace.id ? 0.5 : 1,
                      position: 'relative',
                      cursor: 'grab'
                    }}
                  >
                    {/* 드롭 인디케이터 - before */}
                    {dragState.dropTargetId === workspace.id && dragState.dropPosition === 'before' && (
                      <div
                        style={{
                          position: 'absolute',
                          top: -2,
                          left: 0,
                          right: 0,
                          height: '3px',
                          backgroundColor: designTokens.colors.primary.DEFAULT,
                          borderRadius: '2px'
                        }}
                      />
                    )}

                    {/* 드래그 핸들 */}
                    <div className="flex-shrink-0 cursor-grab">
                      <GripVertical
                        size={16}
                        style={{ color: designTokens.colors.gray[4] }}
                      />
                    </div>

                    {/* 워크스페이스 정보 */}
                    <div className="flex-1 min-w-0">
                      <div
                        className="font-semibold truncate"
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: '14px',
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        {workspace.name}
                        {workspace.isCurrentlySelected && (
                          <span
                            className="ml-2 text-xs"
                            style={{ color: designTokens.colors.primary.DEFAULT }}
                          >
                            (현재)
                          </span>
                        )}
                      </div>
                      <div
                        className="text-xs truncate"
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          color: designTokens.colors.gray[4]
                        }}
                      >
                        {workspace.org_name}
                      </div>
                    </div>

                    {/* 숨기기 버튼 */}
                    <Button
                      color="dark"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleHideClick(workspace)
                      }}
                      disabled={workspace.isCurrentlySelected || isLoading}
                    >
                      탈퇴
                    </Button>

                    {/* 드롭 인디케이터 - after */}
                    {dragState.dropTargetId === workspace.id && dragState.dropPosition === 'after' && (
                      <div
                        style={{
                          position: 'absolute',
                          bottom: -2,
                          left: 0,
                          right: 0,
                          height: '3px',
                          backgroundColor: designTokens.colors.primary.DEFAULT,
                          borderRadius: '2px'
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2.5 flex-shrink-0">
            <Button
              color="dark"
              size="medium"
              onClick={handleClose}
            >
              닫기
            </Button>
          </div>
        </div>
      </div>

      {/* 삭제 확인 모달 */}
      {deleteConfirm.isOpen && deleteConfirm.workspace && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          data-modal-overlay
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            if (e.target === e.currentTarget && !isLoading) {
              setDeleteConfirm({ isOpen: false, workspace: null })
            }
          }}
        >
          <div
            className="bg-white rounded"
            style={{
              width: '400px',
              padding: '20px',
              border: `1px solid ${designTokens.colors.gray[1]}`,
              borderRadius: '4px'
            }}
          >
            <div
              className="flex justify-between items-center"
              style={{
                padding: '7px 0',
                borderBottom: `1px solid ${designTokens.colors.gray[1]}`,
                marginBottom: '16px'
              }}
            >
              <h2
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '24px',
                  fontWeight: 700,
                  lineHeight: '1.193359375em',
                  color: designTokens.colors.gray[6]
                }}
              >
                워크스페이스 탈퇴
              </h2>
              <button
                onClick={() => !isLoading && setDeleteConfirm({ isOpen: false, workspace: null })}
                disabled={isLoading}
                className="p-0 border-0 bg-transparent cursor-pointer"
                style={{
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X
                  size={12}
                  style={{
                    color: designTokens.colors.gray[5],
                    strokeWidth: 2
                  }}
                />
              </button>
            </div>

            <div
              className="flex flex-col items-center justify-center"
              style={{
                padding: '20px 0',
                marginBottom: '16px'
              }}
            >
              <p
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '16px',
                  fontWeight: 600,
                  lineHeight: '1.5em',
                  color: designTokens.colors.black,
                  textAlign: 'center'
                }}
              >
                <strong>{deleteConfirm.workspace.name}</strong> 워크스페이스를
                <br />
                삭제하시겠습니까?
              </p>
              <p
                className="mt-2"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '14px',
                  color: designTokens.colors.gray[4],
                  textAlign: 'center'
                }}
              >
                탈퇴한 워크스페이스는 목록에서 제외됩니다.
              </p>
            </div>

            <div className="flex justify-end gap-2.5">
              <Button
                color="dark"
                size="medium"
                onClick={() => setDeleteConfirm({ isOpen: false, workspace: null })}
                disabled={isLoading}
              >
                취소
              </Button>
              <Button
                color="danger"
                size="medium"
                onClick={handleConfirmHide}
                disabled={isLoading}
              >
                {isLoading ? '처리 중...' : '탈퇴'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default WorkspaceManagementModal
